import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { ThemeContext, type Theme, type ThemeContextValue } from './theme-context';

const STORAGE_KEY = 'bdse-theme';

/**
 * Read the initial theme from localStorage. Falls back to 'light' on:
 *   - SSR environments (no window/localStorage)
 *   - First-ever visit (no stored value)
 *   - Storage read errors (Safari private mode, quota exceeded, etc.)
 *
 * We do not respect `prefers-color-scheme` automatically. Explicit user
 * toggle is the source of truth in v0.7 and we preserve that behavior.
 */
function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage may throw in private mode or with disabled storage.
    // Fall through to default.
  }
  return 'light';
}

/**
 * ThemeProvider mounts at the root of the app (inside AppRouter). It:
 *
 *   1. Reads the initial theme from localStorage synchronously during state
 *      initialization. This prevents a flash of wrong theme on hydration.
 *   2. Applies `data-theme="dark"` or removes it from <html> via
 *      document.documentElement, matching the v0.7 CSS selector
 *      `[data-theme="dark"] ...` exactly.
 *   3. Persists changes back to localStorage.
 *
 * Exports only the component. The context object and hooks live in sibling
 * files (theme-context.ts, use-theme.ts) to satisfy
 * react-refresh/only-export-components.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  // Apply theme to the document element on mount and on every change.
  // Using documentElement (the <html> element) so the [data-theme="dark"]
  // selector in CSS matches all descendants including <body>.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Persistence failures are non-fatal; the theme still applies for the
      // session.
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const value: ThemeContextValue = { theme, toggleTheme, setTheme };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
